import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useCityOptions from "./useCityOptions";
import {
  buildFilterSuggestions,
  FilterSuggestion,
  FilterSuggestionType,
} from "../utils/buildFilterSuggestions";
import {
  companyPropertyOptionsQueryKey,
  companyRouteOptionsQueryKey,
} from "../utils/companyFilterOptionQueryKeys";

type SelectOption = { value: string; label: string };

export type SearchBarSuggestion = {
  key: string;
  label: string;
  icon: React.ReactNode;
  type: FilterSuggestionType;
  value: string;
};

type PropertyHistoryFiltersState = {
  cities?: string[];
  routes?: string[];
  jobs?: string[];
  [key: string]: unknown;
};

const FIELD_BY_TYPE: Record<
  FilterSuggestionType,
  "cities" | "routes" | "jobs"
> = {
  city: "cities",
  route: "routes",
  property: "jobs",
};

const iconClassName = "w-4 h-4 text-snow-blue-3";

function CityIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={iconClassName}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
      />
    </svg>
  );
}

function RouteIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={iconClassName}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z"
      />
    </svg>
  );
}

function PropertyIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={iconClassName}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
      />
    </svg>
  );
}

function iconForType(type: FilterSuggestionType): React.ReactNode {
  if (type === "city") return <CityIcon />;
  if (type === "route") return <RouteIcon />;
  return <PropertyIcon />;
}

function toSearchBarSuggestion(
  suggestion: FilterSuggestion,
): SearchBarSuggestion {
  return {
    key: `${suggestion.type}-${suggestion.value}`,
    label: suggestion.label,
    icon: iconForType(suggestion.type),
    type: suggestion.type,
    value: suggestion.value,
  };
}

/**
 * Property History search autocomplete: matches city / route / property filter
 * options when a single company is selected.
 */
export default function usePropertyHistoryFilterAutocomplete({
  companyId,
  filters,
  initialSearchInput = "",
  onApplyFilters,
}: {
  companyId: string | null;
  filters: PropertyHistoryFiltersState;
  initialSearchInput?: string;
  onApplyFilters: (newFilters: PropertyHistoryFiltersState) => void;
}): {
  searchInput: string;
  setSearchInput: (value: string) => void;
  suggestions: SearchBarSuggestion[];
  onSelectSuggestion: (suggestion: SearchBarSuggestion) => void;
} {
  const [searchInput, setSearchInput] = useState(initialSearchInput);

  const { cityOptions } = useCityOptions(companyId);

  const routeNameOptionsQuery = useQuery<{ _id: string; name: string }[]>({
    queryKey: companyRouteOptionsQueryKey(companyId),
    enabled: companyId !== null,
  });
  const routeOptions: SelectOption[] = routeNameOptionsQuery.isPending
    ? []
    : (routeNameOptionsQuery.data ?? []).map((o) => ({
        value: o._id,
        label: o.name,
      }));

  const propertyOptionsQuery = useQuery<{ _id: string; name: string }[]>({
    queryKey: companyPropertyOptionsQueryKey(companyId),
    enabled: companyId !== null,
  });
  const propertyOptions: SelectOption[] = propertyOptionsQuery.isPending
    ? []
    : (propertyOptionsQuery.data ?? []).map((o) => ({
        value: o._id,
        label: o.name,
      }));

  const suggestions = useMemo(() => {
    if (companyId === null) {
      return [];
    }
    return buildFilterSuggestions(searchInput, {
      cities: cityOptions,
      routes: routeOptions,
      properties: propertyOptions,
    }).map(toSearchBarSuggestion);
  }, [companyId, searchInput, cityOptions, routeOptions, propertyOptions]);

  const onSelectSuggestion = (suggestion: SearchBarSuggestion) => {
    const field = FIELD_BY_TYPE[suggestion.type];
    const existing = (filters[field] as string[] | undefined) ?? [];
    const nextValues = existing.includes(suggestion.value)
      ? existing
      : [...existing, suggestion.value];
    setSearchInput("");
    onApplyFilters({
      ...filters,
      [field]: nextValues,
    });
  };

  return {
    searchInput,
    setSearchInput,
    suggestions,
    onSelectSuggestion,
  };
}
